/**
 * Romaneios — a saída de material para as obras.
 *
 * É o fluxo que o Lucas confirmou estar rodando bem em produção, com romaneios
 * completos. Portado com a regra NOVA de estoque, decidida por ele em 03/08: o
 * físico baixa na RETIRADA, não na entrega.
 *
 * A tela deixa isso explícito em cada estado, porque a mudança altera o que
 * "aguardando retirada" significa — antes o material só saía do número no fim;
 * agora ele já está comprometido desde a criação.
 */

import { api, ErroApi } from '../api.js';
import { escapar, quando, dataHora } from '../comum/formato.js';
import { imprimirRomaneio } from '../comum/imprimir-romaneio.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';

type Status = 'aguardando_retirada' | 'em_rota' | 'entregue' | 'cancelado';

interface RomaneioLista {
  numero: string;
  status: Status;
  finalidade: 'obra' | 'concerto';
  editado_em?: string | null;
  criado_em: string;
  retirado_em: string | null;
  entregue_em: string | null;
  motorista_nome: string | null;
  encarregado: string | null;
  recebedor: string | null;
  obra: string | null;
  obraNome: string | null;
  obraEndereco: string | null;
  itens: number;
}

interface ItemRomaneio {
  codigo: string;
  nome: string;
  unidade: string | null;
  quantidade: number;
  nome_no_ato: string | null;
}

interface Edicao {
  quando: string;
  quem: string | null;
  mudancas: string[];
}

interface RomaneioDetalhe extends Omit<RomaneioLista, 'itens'> {
  finalidade: 'obra' | 'concerto';
  autorizou: string | null;
  obs: string | null;
  editado_em: string | null;
  edicoes: Edicao[];
  /** Pedidos que geraram este romaneio (vazio para romaneio avulso). */
  pedidos?: { numero: string; solicitante: string | null }[];
  foto_retirada_url: string | null;
  lat_retirada: number | null;
  lng_retirada: number | null;
  lat_entrega: number | null;
  lng_entrega: number | null;
  itens: ItemRomaneio[];
  fotosEntrega: string[];
}

/** A imagem do mapa (proxy autenticado) e o link navegável do mesmo ponto. */
function mapaDe(lat: number | null, lng: number | null) {
  if (lat === null || lng === null) return null;
  return {
    img: api.foto(`/api/fotos/mapa?lat=${lat}&lng=${lng}`) ?? '',
    link: `https://www.google.com/maps?q=${lat},${lng}`,
  };
}

const ESTADO: Record<Status, { rotulo: string; classe: string; oQueSignifica: string }> = {
  aguardando_retirada: {
    rotulo: 'Aguardando retirada',
    classe: 'e-aguardando',
    oQueSignifica: 'O material está reservado e já saiu do disponível, mas ainda está no galpão.',
  },
  em_rota: {
    rotulo: 'Em rota',
    classe: 'e-rota',
    oQueSignifica: 'O material saiu do galpão — o saldo físico já foi baixado.',
  },
  entregue: {
    rotulo: 'Entregue',
    classe: 'e-entregue',
    oQueSignifica: 'Chegou na obra. O estoque já tinha sido baixado na retirada.',
  },
  cancelado: {
    rotulo: 'Cancelado',
    classe: 'e-cancelado',
    oQueSignifica: 'Se já tinha sido retirado, o material voltou como movimento de retorno.',
  },
};

export function montarRomaneios(raiz: HTMLElement): void {
  let filtro: Status | 'todos' = 'todos';
  let aberto: string | null = null;

  raiz.innerHTML = `
    <div class="chips" id="filtros"></div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const filtros = raiz.querySelector<HTMLElement>('#filtros')!;
  const lista = raiz.querySelector<HTMLElement>('#lista')!;

  function desenharFiltros() {
    const opcoes: [Status | 'todos', string][] = [
      ['todos', 'Todos'],
      ['aguardando_retirada', 'Aguardando retirada'],
      ['em_rota', 'Em rota'],
      ['entregue', 'Entregues'],
      ['cancelado', 'Cancelados'],
    ];
    filtros.innerHTML = opcoes
      .map(
        ([v, t]) =>
          `<button class="chip" data-f="${v}" aria-pressed="${filtro === v}">${t}</button>`,
      )
      .join('');
  }

  filtros.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-f]');
    if (!b) return;
    filtro = b.dataset['f'] as Status | 'todos';
    desenharFiltros();
    void carregar();
  });

  async function carregar() {
    try {
      const p = new URLSearchParams({ porPagina: '50' });
      if (filtro !== 'todos') p.set('status', filtro);
      const r = await api.get<{ romaneios: RomaneioLista[] }>(`/api/romaneios?${p}`);
      desenhar(r.romaneios);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
    }
  }

  function desenhar(rs: RomaneioLista[]) {
    if (rs.length === 0) {
      lista.innerHTML = `<p class="vazio">Nenhum romaneio${
        filtro === 'todos' ? '' : ' nesse estado'
      }.</p>`;
      return;
    }
    lista.innerHTML = `<div class="romaneios">${rs.map(cartao).join('')}</div>`;
    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((el) => {
      el.addEventListener('click', () => void abrir(el.dataset['abrir']!));
    });
  }

  function cartao(r: RomaneioLista): string {
    const e = ESTADO[r.status];
    // A data que importa muda com o estado: num romaneio parado, o que interessa
    // é há quanto tempo ele espera; num entregue, quando chegou.
    const marco =
      r.status === 'entregue' && r.entregue_em
        ? `Entregue ${quando(r.entregue_em)}`
        : r.status === 'em_rota' && r.retirado_em
          ? `Retirado ${quando(r.retirado_em)}`
          : `Criado ${quando(r.criado_em)}`;
    return `
      <button class="romaneio" data-abrir="${escapar(r.numero)}">
        <div class="romaneio-topo">
          <span class="romaneio-num">${escapar(r.numero)}${
            r.editado_em ? ' <span class="rom-editado" title="Alterado depois de criado">✏️</span>' : ''
          }</span>
          <span class="estado ${e.classe}">${e.rotulo}</span>
        </div>
        <div class="romaneio-obra">${escapar(r.obraNome ?? '🛠️ Concerto de ferramentas')} <span class="sub-inline">${escapar(r.obra ?? '')}</span></div>
        <div class="romaneio-meta">
          ${r.itens} item(ns)${r.motorista_nome ? ` · ${escapar(r.motorista_nome)}` : ''}
        </div>
        <div class="romaneio-marco">${marco}</div>
      </button>`;
  }

  async function abrir(numero: string) {
    aberto = numero;
    lista.innerHTML = `<p class="carregando">Abrindo ${escapar(numero)}…</p>`;
    try {
      const r = await api.get<RomaneioDetalhe>(`/api/romaneios/${encodeURIComponent(numero)}`);
      if (aberto !== numero) return;
      detalhe(r);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir.',
      )}</p>`;
    }
  }

  function detalhe(r: RomaneioDetalhe) {
    const e = ESTADO[r.status];
    lista.innerHTML = `
      <button class="voltar" id="voltar">← Todos os romaneios</button>
      <article class="ficha">
        <header class="ficha-topo">
          <div>
            <span class="rotulo">Romaneio</span>
            <h2>${escapar(r.numero)}${r.editado_em ? ' <span class="rom-editado" title="Este romaneio foi alterado depois de criado">✏️ editado</span>' : ''}</h2>
          </div>
          <div class="ficha-topo-acoes">
            ${
              r.status === 'aguardando_retirada' || r.status === 'em_rota'
                ? `<button class="btn btn-claro" id="editar-rom">✏️ Editar</button>`
                : ''
            }
            <button class="btn btn-claro" id="imprimir-rom">🖨️ Imprimir romaneio</button>
            <span class="estado ${e.classe}">${e.rotulo}</span>
          </div>
        </header>
        <p class="ficha-explica">${e.oQueSignifica}</p>

        <dl class="ficha-dados">
          <div><dt>${r.finalidade === 'concerto' ? 'Finalidade' : 'Obra'}</dt><dd>${escapar(
            r.obraNome ?? 'Concerto de ferramentas',
          )} <span class="sub-inline">${escapar(r.obra ?? '')}</span></dd></div>
          ${r.obraEndereco ? `<div><dt>Endereço</dt><dd>${escapar(r.obraEndereco)}</dd></div>` : ''}
          ${r.encarregado ? `<div><dt>Encarregado</dt><dd>${escapar(r.encarregado)}</dd></div>` : ''}
          ${r.motorista_nome ? `<div><dt>Motorista</dt><dd>${escapar(r.motorista_nome)}</dd></div>` : ''}
          ${r.autorizou ? `<div><dt>Autorizou</dt><dd>${escapar(r.autorizou)}</dd></div>` : ''}
          <div><dt>Criado</dt><dd>${dataHora(r.criado_em)}</dd></div>
          ${r.retirado_em ? `<div><dt>Retirado</dt><dd>${dataHora(r.retirado_em)}</dd></div>` : ''}
          ${r.entregue_em ? `<div><dt>Entregue</dt><dd>${dataHora(r.entregue_em)}</dd></div>` : ''}
          ${r.recebedor ? `<div><dt>Quem recebeu</dt><dd>${escapar(r.recebedor)}</dd></div>` : ''}
        </dl>

        ${r.obs ? `<p class="ficha-obs">${escapar(r.obs)}</p>` : ''}

        <h3>Itens</h3>
        <div class="rolagem">
          <table class="tabela">
            <thead><tr><th>Código</th><th>Item</th><th class="num">Qtd.</th><th>Un.</th></tr></thead>
            <tbody>${r.itens
              .map(
                (i) => `<tr>
                  <td class="cod">${escapar(i.codigo)}</td>
                  <td>${escapar(i.nome)}${
                    i.nome_no_ato && i.nome_no_ato !== i.nome
                      ? `<span class="sub">no romaneio: ${escapar(i.nome_no_ato)}</span>`
                      : ''
                  }</td>
                  <td class="num">${i.quantidade}</td>
                  <td class="un">${escapar(i.unidade ?? '')}</td>
                </tr>`,
              )
              .join('')}</tbody>
          </table>
        </div>

        ${fotos(r)}
        ${mapaEntrega(r)}
        ${
          r.edicoes?.length
            ? `<h3>✏️ O que foi editado</h3>
               <ul class="rom-edicoes">
                 ${r.edicoes
                   .map(
                     (ed) => `<li><b>${dataHora(ed.quando)}</b>${ed.quem ? ` · ${escapar(ed.quem)}` : ''}
                       <ul>${ed.mudancas.map((m) => `<li>${escapar(m)}</li>`).join('')}</ul></li>`,
                   )
                   .join('')}
               </ul>`
            : ''
        }
        <div id="rom-editor"></div>
      </article>`;

    lista.querySelector('#voltar')!.addEventListener('click', () => {
      aberto = null;
      void carregar();
    });
    lista.querySelector('#imprimir-rom')!.addEventListener('click', () => imprimirRomaneio(r));
    lista.querySelector('#editar-rom')?.addEventListener('click', () => void abrirEditor(r));
  }

  /**
   * O EDITOR do romaneio (14/08, Lucas): motorista, encarregado, observação —
   * e, enquanto aguarda retirada, as quantidades (0 remove). Toda mudança fica
   * registrada e o card ganha a marca de editado.
   */
  async function abrirEditor(r: RomaneioDetalhe): Promise<void> {
    const caixa = lista.querySelector<HTMLElement>('#rom-editor');
    if (!caixa || caixa.dataset['aberto']) return;
    caixa.dataset['aberto'] = '1';

    let motoristas: { id: number; nome: string }[] = [];
    try {
      const rp = await api.get<{ pessoas: { id: number; nome: string; modulos: string[] }[] }>(
        '/api/pessoas?status=ativo',
      );
      motoristas = rp.pessoas.filter((x) => x.modulos.includes('motorista'));
    } catch {
      /* sem lista, o avulso resolve */
    }
    const cadastrado = motoristas.some((m) => m.nome === r.motorista_nome);
    const podeItens = r.status === 'aguardando_retirada';

    caixa.innerHTML = `
      <section class="rom-editor">
        <h3>✏️ Editar romaneio</h3>
        <div class="form-2col">
          <label class="rotulado"><span>Motorista</span>
            <select id="re-motorista">
              ${motoristas
                .map(
                  (m) =>
                    `<option value="${escapar(m.nome)}" ${m.nome === r.motorista_nome ? 'selected' : ''}>${escapar(m.nome)}</option>`,
                )
                .join('')}
              <option value="__avulso__" ${!cadastrado && r.motorista_nome ? 'selected' : ''}>✏️ Outro (sem cadastro)</option>
            </select>
            <input id="re-motorista-avulso" maxlength="120" ${cadastrado || !r.motorista_nome ? 'hidden' : ''}
                   value="${!cadastrado ? escapar(r.motorista_nome ?? '') : ''}"
                   placeholder="Nome do motorista sem cadastro"></label>
          <label class="rotulado"><span>Encarregado</span>
            <input id="re-encarregado" maxlength="120" value="${escapar(r.encarregado ?? '')}"></label>
        </div>
        <label class="rotulado"><span>Observação</span>
          <input id="re-obs" maxlength="1000" value="${escapar(r.obs ?? '')}"></label>
        ${
          podeItens
            ? `<h4>Itens <span class="sub-inline">0 remove o item</span></h4>
               <div class="rom-editor-itens">
                 ${r.itens
                   .map(
                     (i) => `<label class="rotulado"><span>${escapar(i.nome_no_ato ?? i.nome)} <i class="sub-inline">${escapar(i.codigo)}</i></span>
                       <input type="number" min="0" step="any" inputmode="decimal"
                              value="${i.quantidade}" data-re-item="${escapar(i.codigo)}"></label>`,
                   )
                   .join('')}
               </div>`
            : `<p class="dica">O material já está na rua — itens não mudam mais; motorista, encarregado e observação sim.</p>`
        }
        <div class="conf-acoes">
          <button class="btn" id="re-salvar">✓ Salvar edição</button>
          <button class="btn btn-claro" id="re-cancelar">Cancelar</button>
        </div>

        <details class="rom-cancelar">
          <summary>🗑️ Cancelar este romaneio…</summary>
          <p class="dica">${
            r.status === 'aguardando_retirada'
              ? 'Nada saiu do galpão: os itens voltam a ficar disponíveis na hora.'
              : 'O material já está na rua: o cancelamento registra o RETORNO de cada item ao estoque.'
          }</p>
          <label class="rotulado"><span>Motivo (o solicitante lê)</span>
            <input id="rc-motivo" maxlength="500" placeholder="Ex.: obra adiou o recebimento"></label>
          ${
            r.pedidos?.length
              ? `<label class="rotulado"><span>Pedido ${escapar(r.pedidos.map((x) => x.numero).join(', '))}${
                  r.pedidos[0]?.solicitante ? ` de ${escapar(r.pedidos[0].solicitante)}` : ''
                }</span>
                 <select id="rc-pedido">
                   <option value="refazer">volta para a fila de separação (separar de novo)</option>
                   <option value="cancelar">é cancelado junto</option>
                 </select></label>`
              : ''
          }
          <div class="conf-acoes">
            <button class="btn btn-perigo" id="rc-confirmar">Cancelar o romaneio ${escapar(r.numero)}</button>
          </div>
        </details>
      </section>`;
    caixa.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const sel = caixa.querySelector<HTMLSelectElement>('#re-motorista')!;
    const avulso = caixa.querySelector<HTMLInputElement>('#re-motorista-avulso')!;
    sel.addEventListener('change', () => {
      avulso.hidden = sel.value !== '__avulso__';
      if (!avulso.hidden) avulso.focus();
    });

    caixa.querySelector('#re-cancelar')!.addEventListener('click', () => {
      caixa.innerHTML = '';
      delete caixa.dataset['aberto'];
    });

    caixa.querySelector('#rc-confirmar')!.addEventListener('click', () => {
      const motivo = caixa.querySelector<HTMLInputElement>('#rc-motivo')!.value.trim();
      if (motivo.length < 3) return void avisarBloqueante('Diga o motivo do cancelamento.');
      const pedido = caixa.querySelector<HTMLSelectElement>('#rc-pedido')?.value ?? 'refazer';
      if (!confirm(`Cancelar o romaneio ${r.numero}? Isso não tem volta.`)) return;
      void api
        .post<{ pedidos: string[]; pedidoDestino: string; recolocadoNoEstoque: boolean }>(
          `/api/romaneios/${encodeURIComponent(r.numero)}/cancelamento`,
          { motivo, pedido },
        )
        .then((resp) => {
          const ped = resp.pedidos.length
            ? resp.pedidoDestino === 'cancelar'
              ? ` Pedido ${resp.pedidos.join(', ')} cancelado junto.`
              : ` Pedido ${resp.pedidos.join(', ')} voltou para a separação.`
            : '';
          avisar(`Romaneio ${r.numero} cancelado.${ped}`);
          return abrir(r.numero);
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui cancelar.'));
    });

    caixa.querySelector('#re-salvar')!.addEventListener('click', () => {
      const motorista = sel.value === '__avulso__' ? avulso.value.trim() : sel.value.trim();
      if (!motorista) return void avisarBloqueante('O romaneio precisa de um motorista.');
      const corpo: Record<string, unknown> = {
        motorista,
        encarregado: caixa.querySelector<HTMLInputElement>('#re-encarregado')!.value.trim(),
        obs: caixa.querySelector<HTMLInputElement>('#re-obs')!.value.trim(),
      };
      if (podeItens) {
        corpo['itens'] = [...caixa.querySelectorAll<HTMLInputElement>('[data-re-item]')].map((c) => ({
          codigo: c.dataset['reItem']!,
          quantidade: Number(c.value) || 0,
        }));
      }
      void api
        .patch<{ mudancas: string[] }>(`/api/romaneios/${encodeURIComponent(r.numero)}`, corpo)
        .then((resp) => {
          if (resp.mudancas.length === 0) {
            avisar('Nada mudou — o romaneio ficou como estava.');
          } else {
            avisar(`Editado: ${resp.mudancas.join('; ')}`);
          }
          return abrir(r.numero);
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui salvar.'));
    });
  }

  /** O mapa do ponto onde a entrega foi FINALIZADA (12/08, Lucas). */
  function mapaEntrega(r: RomaneioDetalhe): string {
    const mapa = mapaDe(r.lat_entrega, r.lng_entrega);
    if (!mapa) {
      return r.status === 'entregue'
        ? `<p class="dica">A entrega não registrou localização (o motorista não liberou o GPS).</p>`
        : '';
    }
    return `
      <h3>📍 Onde a entrega foi finalizada</h3>
      <a href="${escapar(mapa.link)}" target="_blank" rel="noopener" class="mp-rom-mapa">
        <img src="${escapar(mapa.img)}" alt="Mapa do ponto da entrega">
      </a>
      <p class="dica">Clique no mapa para abrir no Google Maps.</p>`;
  }


  function fotos(r: RomaneioDetalhe): string {
    const todas = [
      ...(r.foto_retirada_url ? [{ url: r.foto_retirada_url, legenda: 'Retirada' }] : []),
      ...r.fotosEntrega.map((u) => ({ url: u, legenda: 'Entrega' })),
    ];
    if (todas.length === 0) return '';
    return `
      <h3>Fotos</h3>
      <div class="fotos">
        ${todas
          .map(
            (f) => `<figure>
              <img src="${escapar(api.foto(f.url) ?? '')}" alt="${f.legenda}" loading="lazy"
                   onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'sem',textContent:'foto indisponível'}))">
              <figcaption>${f.legenda}</figcaption>
            </figure>`,
          )
          .join('')}
      </div>`;
  }

  desenharFiltros();
  void carregar();
}
